/* ==========================================================================
   <ef-carousel> — accessible slide rotator
   Autoplay pauses on hover, on focus, when the tab is hidden, and whenever the
   visitor has asked for reduced motion. Slides stay in the DOM and are hidden
   with [hidden] so assistive tech only sees the active one.
   ========================================================================== */

(function () {
  'use strict';

  if (!('customElements' in window)) return;

  customElements.define(
    'ef-carousel',
    class extends HTMLElement {
      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('[data-ef-slide]'));
        if (this.slides.length === 0) return;

        this.dots = Array.from(this.querySelectorAll('[data-ef-dot]'));
        this.prevBtn = this.querySelector('[data-ef-prev]');
        this.nextBtn = this.querySelector('[data-ef-next]');
        this.toggleBtn = this.querySelector('[data-ef-toggle]');
        this.liveRegion = this.querySelector('[data-ef-live]');

        this.index = 0;
        this.timer = null;
        this.interval = parseInt(this.getAttribute('data-interval'), 10) || 6000;

        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        // Autoplay is opt-in via the attribute, and never runs for reduced motion.
        this.autoplayWanted = this.getAttribute('data-autoplay') === 'true' && !this.reduceMotion;
        this.playing = false;

        this.bind();
        this.show(0, { silent: true });

        if (this.autoplayWanted && this.slides.length > 1) this.play();
      }

      disconnectedCallback() {
        this.pause();
        document.removeEventListener('visibilitychange', this.onVisibility);
      }

      bind() {
        this.onVisibility = () => {
          if (document.hidden) {
            this.stopTimer();
          } else if (this.playing) {
            this.startTimer();
          }
        };

        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1, true));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1, true));

        if (this.toggleBtn) {
          this.toggleBtn.addEventListener('click', () => {
            this.playing ? this.pause() : this.play();
          });
        }

        this.dots.forEach((dot, i) => {
          dot.addEventListener('click', () => {
            this.show(i);
            this.pause();
          });
        });

        // Hover and focus suspend the timer without changing the play/pause state
        this.addEventListener('mouseenter', () => this.stopTimer());
        this.addEventListener('mouseleave', () => {
          if (this.playing) this.startTimer();
        });
        this.addEventListener('focusin', () => this.stopTimer());
        this.addEventListener('focusout', (event) => {
          if (this.contains(event.relatedTarget)) return;
          if (this.playing) this.startTimer();
        });

        this.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.step(-1, true);
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.step(1, true);
          }
        });

        document.addEventListener('visibilitychange', this.onVisibility);
      }

      show(next, options) {
        var opts = options || {};
        var total = this.slides.length;
        this.index = ((next % total) + total) % total;

        this.slides.forEach((slide, i) => {
          var active = i === this.index;
          slide.hidden = !active;
          slide.classList.toggle('is-active', active);
        });

        this.dots.forEach((dot, i) => {
          var active = i === this.index;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-current', active ? 'true' : 'false');
        });

        if (this.liveRegion && !opts.silent) {
          this.liveRegion.textContent = 'Slide ' + (this.index + 1) + ' of ' + total;
        }

        this.syncMedia();
      }

      /* A slide can hold a video, and two things have to be true of it: the
         one on screen is playing, and the ones behind [hidden] are not — an
         off-screen slide left running burns bandwidth on a phone for a frame
         nobody can see.

         `autoplay` alone is not enough. Muted + playsinline is ALLOWED to
         autoplay, but a browser may still decline when the element has not
         buffered enough yet, and it then sits on the poster with no error and
         readyState 3 — which is exactly how this shipped: playing on one load,
         frozen on the next. Ask explicitly, and swallow the rejection, because
         a refused play() rejects and an unhandled rejection is a console error
         for something that is not broken. */
      syncMedia() {
        this.slides.forEach((slide, i) => {
          var video = slide.querySelector('video');
          if (!video) return;
          if (i === this.index) {
            var attempt = video.play();
            if (attempt && attempt.catch) attempt.catch(function () {});
          } else if (!video.paused) {
            video.pause();
          }
        });
      }

      step(delta, userInitiated) {
        this.show(this.index + delta);
        // A manual nudge should not fight the timer mid-cycle
        if (userInitiated && this.playing) this.startTimer();
      }

      play() {
        this.playing = true;
        this.startTimer();
        this.syncToggle();
      }

      pause() {
        this.playing = false;
        this.stopTimer();
        this.syncToggle();
      }

      startTimer() {
        this.stopTimer();
        this.timer = window.setInterval(() => this.step(1), this.interval);
      }

      stopTimer() {
        if (this.timer) {
          window.clearInterval(this.timer);
          this.timer = null;
        }
      }

      syncToggle() {
        if (!this.toggleBtn) return;

        var label = this.playing
          ? this.toggleBtn.getAttribute('data-label-pause')
          : this.toggleBtn.getAttribute('data-label-play');

        this.toggleBtn.setAttribute('aria-label', label || '');
        this.toggleBtn.setAttribute('aria-pressed', this.playing ? 'false' : 'true');

        var playIcon = this.toggleBtn.querySelector('[data-icon-play]');
        var pauseIcon = this.toggleBtn.querySelector('[data-icon-pause]');
        if (playIcon) playIcon.hidden = this.playing;
        if (pauseIcon) pauseIcon.hidden = !this.playing;
      }
    }
  );
})();
